import React, { useState } from "react";
import { Box, Chip } from "@mui/material";
import { INDIGO, SLATE } from "../constants/Colors";

/**
 * Tag/chip input for array data type.
 * Props:
 *   value    – JSON string representing an array, e.g. '["a","b"]'
 *   onChange – called with the updated JSON string
 *   placeholder – hint text shown when no items exist
 */
export default function ArrayChipInput({ value, onChange, placeholder }) {
  const [inputVal, setInputVal] = useState("");

  let items = [];
  try {
    const parsed = JSON.parse(value || "[]");
    if (Array.isArray(parsed)) items = parsed;
  } catch {
    items = [];
  }

  const commit = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed || items.includes(trimmed)) return;
    onChange(JSON.stringify([...items, trimmed]));
  };

  const removeItem = (idx) => {
    onChange(JSON.stringify(items.filter((_, i) => i !== idx)));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(inputVal);
      setInputVal("");
    } else if (e.key === "Backspace" && !inputVal && items.length > 0) {
      removeItem(items.length - 1);
    }
  };

  return (
    <Box
      sx={{
        border: `1px solid ${SLATE[300]}`,
        borderRadius: "8px",
        p: "8px 12px",
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        alignItems: "center",
        bgcolor: "#ffffff",
        minHeight: "42px",
        cursor: "text",
        "&:focus-within": { borderColor: INDIGO[500] },
      }}
      onClick={(e) => e.currentTarget.querySelector("input")?.focus()}
    >
      {items.map((item, idx) => (
        <Chip
          key={idx}
          label={item}
          size="small"
          onDelete={() => removeItem(idx)}
          sx={{
            bgcolor: INDIGO[50],
            color: INDIGO[700],
            borderRadius: "6px",
            fontSize: "0.8125rem",
            height: "26px",
            "& .MuiChip-deleteIcon": { color: INDIGO[400], "&:hover": { color: INDIGO[600] } },
          }}
        />
      ))}
      <input
        value={inputVal}
        onChange={(e) => setInputVal(e.target.value.trimStart())}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (inputVal.trim()) { commit(inputVal); setInputVal(""); }
        }}
        placeholder={items.length === 0 ? (placeholder || "Type and press Enter to add…") : ""}
        style={{
          flex: 1,
          minWidth: "120px",
          border: "none",
          outline: "none",
          fontSize: "0.875rem",
          color: SLATE[900],
          background: "transparent",
          fontFamily: "inherit",
          padding: "2px 0",
        }}
      />
    </Box>
  );
}
